import { useEffect, useRef, useState } from 'react';
import { ObsEncoderInfo } from '../../types/obs';
import { promisefy } from '../../utils/promisefy';
import { ObsEncoderClassSelector } from './ObsEncoderClassSelector';
import { ObsPropertiesEditor } from './ObsPropertiesEditor';
import { getIsEncoderSettingsEnabled, getIsEncoderPropertiesQueryEnabled } from '../../constants/common';
import { getAvailableEncoders } from '../../constants/encoders';

const encoderFieldSuffixMap = {
  bitrate: 'Kbps',
};

export function ObsEncoderEditor({type, defaultValue: defaultValue, disabled, onChange} : {
    type: 'audio' | 'video',
    defaultValue: Pick<ObsEncoderInfo, 'class' | 'settings'> | undefined | null,
    disabled?: boolean,
    onChange?: (value: ObsEncoderInfo) => void,
}) {
    const [hasAdvancedEncodingSettingsAccess, setHasAdvancedEncodingSettingsAccess] = useState(false);
    const [hasEncodingPropertiesQuery, setHasEncodingPropertiesQuery] = useState(false);
    const [availableEncoders, setAvailableEncoders] = useState<ObsEncoderInfo[]>([]);
    const value = useRef<ObsEncoderInfo | null | undefined>(null);

    useEffect(() => {
      if (availableEncoders.length) {
        return;
      }

      const abortController = new AbortController();

      getAvailableEncoders(type).then((encoders) => {
        if (abortController.signal.aborted) {
          return;
        }

        setAvailableEncoders(encoders.filter((item) => {
          return item.class === defaultValue?.class || !item.isDeprecated;
        }));

        if (!value.current) {
          if (defaultValue) {
            value.current = encoders.find((encoder) => encoder.class === defaultValue.class);

            if (value.current) {
              value.current.settings = defaultValue.settings;
            }
          } 
          
          if (!value.current) {
            value.current = encoders[0];
          }
        
          onChange?.(value.current);
        }
      });

      return () => {
        abortController.abort();
      };
    }, [defaultValue, availableEncoders, onChange, type]);

    useEffect(() => {
      if (hasAdvancedEncodingSettingsAccess) {
        return;
      }
  
      const abortController = new AbortController();
  
      Promise.all([
        getIsEncoderSettingsEnabled(),
        getIsEncoderPropertiesQueryEnabled(),
      ]).then(([ encoderSettingsEnabled, encoderPropsQueryEnabled ]) => {
        if (abortController.signal.aborted) {
          return;
        }
  
        if (encoderSettingsEnabled !== hasAdvancedEncodingSettingsAccess) {
          setHasAdvancedEncodingSettingsAccess(encoderSettingsEnabled);
        }

        if (encoderPropsQueryEnabled !== hasEncodingPropertiesQuery) {
          setHasEncodingPropertiesQuery(encoderPropsQueryEnabled);
        }
      });
  
      return () => {
        abortController.abort();
      };
    }, [hasAdvancedEncodingSettingsAccess, hasEncodingPropertiesQuery]);

    return (<>
      <ObsEncoderClassSelector
        disabled={disabled}
        type={type}
        label={type === 'video' ? 'Video Encoding' : 'Audio Encoding'}
        value={value.current?.class}
        onChange={(encoderClass) => {
          const curr = availableEncoders.find((item) => item.class === encoderClass);

          if (curr) {
            value.current = curr;

            onChange?.(value.current);
          }
        }}
      />

      { hasAdvancedEncodingSettingsAccess && <>
        <ObsPropertiesEditor
          disabled={disabled}
          properties={value.current?.properties ?? []}
          settings={value.current?.settings ?? {}}
          defaultSettings={value.current?.defaultSettings ?? {}}
          suffixes={encoderFieldSuffixMap ?? {}}
          onChange={async (data) => {
            let curr: ObsEncoderInfo | null | undefined = value.current;

            if (!curr) {
              return;
            }

            curr.settings = data;

            if (hasEncodingPropertiesQuery) {
              const promise = type === 'video' ? promisefy(window.host.getAvailableVideoEncoderClassProperties) : promisefy(window.host.getAvailableAudioEncoderClassProperties);

              curr = await promise(curr);
            }

            if (!curr) {
              return;
            }

            value.current = curr;

            onChange?.(value.current);
          }}
        />
      </> }
    </>);
}
